import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Activity, AlertTriangle, DollarSign, Smartphone } from 'lucide-react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useOrgStore } from '../store/useOrgStore';
import { StatCard } from '../components/ui/StatCard';
import { EmptyState } from '../components/ui/EmptyState';
import { RiskBadge } from '../components/ui/RiskBadge';
import { CollapsibleSection } from '../components/ui/CollapsibleSection';
import type { ActivityResult, ActivitySegment } from '../types/org.types';

const SEGMENT_LABEL: Record<ActivitySegment, string> = {
  power: 'Power',
  regular: 'Regular',
  occasional: 'Occasional',
  ghost: 'Ghost',
  shadow: 'Shadow',
};

const SEGMENT_DESC: Record<ActivitySegment, string> = {
  power: 'Daily, high CRM activity. Champion users.',
  regular: 'Consistent, moderate activity. Healthy.',
  occasional: 'Sporadic logins. Re-engagement risk.',
  ghost: 'No login in 90 days but still licensed. Cost exposure.',
  shadow: 'Logs in but no CRM work. Using SF as a directory.',
};

const SEGMENT_HEX: Record<ActivitySegment, string> = {
  power: '#3FA96B',
  regular: '#9A988F',
  occasional: '#C7851A',
  ghost: '#D64545',
  shadow: '#3D55D6',
};

const SEGMENT_BADGE: Record<ActivitySegment, 'healthy' | 'medium' | 'critical' | 'neutral'> = {
  power: 'healthy',
  regular: 'neutral',
  occasional: 'medium',
  ghost: 'critical',
  shadow: 'medium',
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatDate(date: string | null) {
  if (!date) return 'Never';
  return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function UserActivityPage({ data }: { data?: ActivityResult | null } = {}) {
  const storeActivity = useOrgStore((s) => s.audit.activity);
  const scanMode = data !== undefined;
  const activity = scanMode ? data : storeActivity;

  const heatmapMax = useMemo(() => {
    if (!activity) return 0;
    return Math.max(0, ...activity.loginHeatmap.map((c) => c.count));
  }, [activity]);

  const heatmapByDayHour = useMemo(() => {
    const m = new Map<string, number>();
    if (!activity) return m;
    for (const c of activity.loginHeatmap) m.set(`${c.day}-${c.hour}`, c.count);
    return m;
  }, [activity]);

  if (!activity)
    return (
      <EmptyState
        title="User activity not loaded"
        hint="Reconnect to scan again."
        icon={<Activity size={20} />}
      />
    );

  const t = activity.totals;
  const dauMauTone = t.dauMauRatio < 0.1 ? 'sunset' : t.dauMauRatio < 0.2 ? 'sunrise' : 'grow';

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-adopt mb-1">User Activity</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Adoption Intelligence</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-3xl">
          Login patterns, CRM activity, and licence-cost exposure across every active user. The
          fastest cost + adoption conversation a TYRL consultant can have.
        </p>
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3"
      >
        {[
          <StatCard
            key="mau"
            label="MAU (last 30d)"
            value={t.mauLast30.toLocaleString()}
            hint={`of ${t.licensedUsers.toLocaleString()} licensed`}
            accent="adopt"
          />,
          <StatCard
            key="dau"
            label="DAU avg"
            value={t.dauAverageLast30.toFixed(1)}
            hint="distinct users / day"
            accent="adopt"
          />,
          <StatCard
            key="ratio"
            label="DAU / MAU"
            value={t.dauMauRatio.toFixed(2)}
            hint={t.dauMauRatio >= 0.2 ? 'Sticky' : 'Re-engagement signal'}
            accent={dauMauTone}
          />,
          <StatCard
            key="ghost"
            label="Ghost users"
            value={activity.segmentCounts.ghost}
            hint="0 logins in 90d"
            accent={activity.segmentCounts.ghost > 0 ? 'sunset' : 'grow'}
            icon={<AlertTriangle size={14} />}
          />,
          <StatCard
            key="shadow"
            label="Shadow users"
            value={activity.segmentCounts.shadow}
            hint="Logging in, no CRM work"
            accent={activity.segmentCounts.shadow > 0 ? 'sunrise' : 'grow'}
          />,
        ].map((card, i) => (
          <motion.div
            key={i}
            variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.25 } } }}
          >
            {card}
          </motion.div>
        ))}
      </motion.div>

      {activity.ghostCost.totalUsers > 0 && (
        <div className="bg-sunset/5 border border-sunset/30 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <DollarSign size={18} className="text-sunset mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-sunset">
                {activity.ghostCost.totalUsers} ghost user{activity.ghostCost.totalUsers === 1 ? '' : 's'}
                {' '}detected
              </div>
              {activity.ghostCost.estimatedAnnualAud > 0 ? (
                <>
                  <div className="text-3xl font-semibold text-crux-light mt-1 tabular-nums">
                    AUD ${activity.ghostCost.estimatedAnnualAud.toLocaleString()}
                    <span className="text-sm text-neutral-500 ml-2">
                      / yr · {activity.ghostCost.pricedUsers} priced user
                      {activity.ghostCost.pricedUsers === 1 ? '' : 's'}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-sm text-neutral-400 mt-1">
                  No licence costs configured. See notes below to set
                  <code className="px-1 mx-1 bg-surface-2 border border-border rounded text-[11px] text-crux-light">
                    LICENSE_COSTS_AUD
                  </code>
                  in .env to estimate annual exposure.
                </div>
              )}
              <div className="mt-3 text-xs text-neutral-500">By licence type:</div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {activity.ghostCost.byLicense.map((row) => (
                  <div
                    key={row.licenseName}
                    className="bg-surface-2 border border-border rounded-md px-3 py-2 flex items-center justify-between gap-3"
                  >
                    <span className="text-crux-light truncate">{row.licenseName}</span>
                    <span className="text-neutral-400 tabular-nums shrink-0">
                      {row.pricingSource === 'free'
                        ? `${row.users} × FREE`
                        : row.pricingSource === 'configured'
                          ? `${row.users} × AUD $${row.perUserAud?.toLocaleString()} = AUD $${row.subtotal?.toLocaleString()}`
                          : `${row.users} · cost not configured`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface-1 border border-border rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
            Monthly Active Users, last 6 months
          </div>
          {activity.mauTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={activity.mauTrend}>
                <XAxis dataKey="month" tick={{ fill: '#6b6b66', fontSize: 10 }} stroke="#DAD8D1" />
                <YAxis tick={{ fill: '#6b6b66', fontSize: 10 }} stroke="#DAD8D1" />
                <Tooltip
                  contentStyle={{ background: '#E7E5DF', border: '1px solid #DAD8D1', borderRadius: 0 }}
                  itemStyle={{ color: '#1B1B1B' }}
                />
                <Bar dataKey="users" fill="#9A988F" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-neutral-500 py-6">No login history available.</div>
          )}
        </div>

        <div className="bg-surface-1 border border-border rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
            User Segments
          </div>
          <div className="space-y-2">
            {(['power', 'regular', 'occasional', 'ghost', 'shadow'] as ActivitySegment[]).map((seg) => {
              const count = activity.segmentCounts[seg];
              const pct = activity.segmentPercents[seg];
              return (
                <div key={seg} className="flex items-center gap-3">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: SEGMENT_HEX[seg] }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-crux-light">{SEGMENT_LABEL[seg]}</span>
                      <RiskBadge tone={SEGMENT_BADGE[seg]}>
                        {count} · {pct.toFixed(0)}%
                      </RiskBadge>
                    </div>
                    <div className="text-xs text-neutral-500 truncate">{SEGMENT_DESC[seg]}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-surface-1 border border-border rounded-lg p-6 overflow-x-auto">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
          Login heatmap, last 90 days · day × hour (UTC)
        </div>
        {heatmapMax > 0 ? (
          <table className="text-[10px] tabular-nums">
            <thead>
              <tr className="text-neutral-600">
                <th className="text-left pr-3"></th>
                {Array.from({ length: 24 }).map((_, h) => (
                  <th key={h} className="px-1 font-medium text-center">
                    {h % 3 === 0 ? h : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                <tr key={d}>
                  <td className="pr-3 text-neutral-500 font-medium">{DAY_LABELS[d]}</td>
                  {Array.from({ length: 24 }).map((_, h) => {
                    const count = heatmapByDayHour.get(`${d}-${h}`) ?? 0;
                    const pct = count / heatmapMax;
                    const bg = pct === 0
                      ? '#E7E5DF'
                      : `rgba(6, 27, 176, ${0.18 + pct * 0.82})`;
                    return (
                      <td
                        key={h}
                        title={`${DAY_LABELS[d]} ${h}:00, ${count} login${count === 1 ? '' : 's'}`}
                        className="w-4 h-4 border border-surface-0"
                        style={{ background: bg }}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-sm text-neutral-500">No login data sampled.</div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface-1 border border-border rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4 flex items-center gap-2">
            <Smartphone size={12} /> Login platforms (last 90d)
          </div>
          <div className="space-y-2">
            {activity.loginPlatforms.length > 0 ? (
              activity.loginPlatforms.map((p) => {
                const total = activity.loginPlatforms.reduce((a, b) => a + b.count, 0);
                const pct = total > 0 ? (p.count / total) * 100 : 0;
                return (
                  <div key={p.platform} className="flex items-center gap-3 text-sm">
                    <span className="text-neutral-400 w-32 truncate">{p.platform}</span>
                    <div className="flex-1 h-1.5 bg-surface-3 rounded-full overflow-hidden">
                      <div className="h-full bg-adopt" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-crux-light tabular-nums w-20 text-right">
                      {p.count.toLocaleString()} · {pct.toFixed(0)}%
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-neutral-500">No platform data.</div>
            )}
          </div>
        </div>

        <div className="bg-surface-1 border border-border rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
            Login failures (last 30d)
          </div>
          {activity.loginFailures ? (
            <div className="space-y-3">
              <div className="flex items-baseline gap-3">
                <div className={`text-3xl font-semibold tabular-nums ${activity.loginFailures.failurePct > 10 ? 'text-sunset' : activity.loginFailures.failurePct > 3 ? 'text-sunrise' : 'text-grow'}`}>
                  {activity.loginFailures.failurePct.toFixed(1)}%
                </div>
                <div className="text-sm text-neutral-500">
                  {activity.loginFailures.failures.toLocaleString()} of {activity.loginFailures.total.toLocaleString()} attempts failed
                </div>
              </div>
              {activity.loginFailures.topFailingUsers.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Top offenders</div>
                  <ul className="text-sm space-y-1">
                    {activity.loginFailures.topFailingUsers.map((u) => (
                      <li key={u.userId} className="flex justify-between">
                        <span className="text-crux-light truncate">{u.userName ?? u.userId}</span>
                        <span className="text-neutral-400 tabular-nums">{u.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-neutral-500">No login status data.</div>
          )}
        </div>
      </div>

      <CollapsibleSection
        title="Top users by 30-day record activity"
        meta={`${Math.min(activity.topUsers.length, 25)} shown of ${activity.topUsers.length}`}
        defaultOpen={false}
      >
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-2">
              <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                <th className="text-left px-4 py-3 font-medium">User</th>
                <th className="text-left px-4 py-3 font-medium">Profile</th>
                <th className="text-left px-4 py-3 font-medium">License</th>
                <th className="text-right px-4 py-3 font-medium">Login days (30d)</th>
                <th className="text-right px-4 py-3 font-medium">Records (30d)</th>
                <th className="text-left px-4 py-3 font-medium">Segment</th>
                <th className="text-left px-4 py-3 font-medium">Last login</th>
              </tr>
            </thead>
            <tbody>
              {activity.topUsers.slice(0, 25).map((u, i) => (
                <tr key={u.userId} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
                  <td className="px-4 py-2.5 text-crux-light">{u.name}</td>
                  <td className="px-4 py-2.5 text-neutral-400">{u.profileName ?? '-'}</td>
                  <td className="px-4 py-2.5 text-neutral-400">{u.licenseName ?? '-'}</td>
                  <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">{u.loginDaysIn30}</td>
                  <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">{u.recordsCreatedIn30}</td>
                  <td className="px-4 py-2.5">
                    <RiskBadge tone={SEGMENT_BADGE[u.segment]}>{SEGMENT_LABEL[u.segment]}</RiskBadge>
                  </td>
                  <td className="px-4 py-2.5 text-neutral-400">{formatDate(u.lastLoginDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      {activity.notes.length > 0 && (
        <div className="text-[11px] text-neutral-600 space-y-1">
          {activity.notes.map((n, i) => (
            <p key={i}>· {n}</p>
          ))}
        </div>
      )}
    </div>
  );
}
